import { useState } from 'react'
import type { Control, FieldPath, FieldValues } from 'react-hook-form'
import { useQuery } from '@tanstack/react-query'
import { Check, ChevronsUpDown } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { FormField } from '@/shared/components/crud/form-field'
import { Button } from '@/shared/components/ui/button'
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/shared/components/ui/popover'
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@/shared/components/ui/command'
import { apiClient } from '@/shared/lib/api/api-client'

export interface InstitutionOption {
  Id: number
  Name: string
  Bic?: string
}

interface WizardInstitutionFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> {
  control: Control<TFieldValues>
  name: TName
  /** Empty scopes the list to nothing: the catalog then answers with every institution. */
  countryCode: string
  onSelect: (institution: InstitutionOption) => void
  /** Defaults to the wizard's own wording; the counterparty form passes its translated label. */
  label?: string
}

/**
 * Searchable institution combobox used by every non-Open-Banking connectivity
 * (Manual Upload, Swift, API, SFTP, Email). The list is scoped to the wizard's
 * selected country: `/api/institutions/all?countryCode=XX` returns that
 * country's institutions plus every country-less one (custom tenant banks and
 * universal catalog entries without a country). Open Banking
 * keeps its own country-scoped card list (`WizardInstitutionList` via
 * `WizardOpenBankingPanel`) and does not use this field. Selecting an entry
 * reports it back through `onSelect` so the caller can autofill the
 * still-editable BIC field.
 */
export function WizardInstitutionField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  control, name, countryCode, onSelect, label = 'Institution',
}: WizardInstitutionFieldProps<TFieldValues, TName>) {
  const [popoverOpen, setPopoverOpen] = useState(false)

  const { data: institutions = [] } = useQuery<InstitutionOption[]>({
    queryKey: ['institutions-all', countryCode],
    queryFn: () => apiClient.get<InstitutionOption[]>(`/api/institutions/all?countryCode=${encodeURIComponent(countryCode)}`),
    staleTime: 10 * 60 * 1000,
  })

  return (
    <FormField control={control} name={name} label={label} required>
      {(field) => {
        const selectedInstitutionName = institutions.find((institution) => institution.Id === field.value)?.Name

        function handleSelect(institution: InstitutionOption) {
          field.onChange(institution.Id)
          onSelect(institution)
          setPopoverOpen(false)
        }

        return (
          <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                role="combobox"
                aria-expanded={popoverOpen}
                aria-label={label}
                className={cn('w-full justify-between font-normal', !field.value && 'text-muted-foreground')}
              >
                {selectedInstitutionName ?? 'Search institution…'}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
              <Command>
                <CommandInput placeholder="Search institution…" />
                <CommandList>
                  <CommandEmpty>No institution found.</CommandEmpty>
                  <CommandGroup>
                    {institutions.map((institution) => (
                      <CommandItem
                        key={institution.Id}
                        value={institution.Name}
                        onSelect={() => handleSelect(institution)}
                      >
                        <Check
                          className={cn('mr-2 h-4 w-4', field.value === institution.Id ? 'opacity-100' : 'opacity-0')}
                        />
                        {institution.Name}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        )
      }}
    </FormField>
  )
}
